import { ArrowLeft, Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const ContactPage = () => {
  return (
    <div className="bg-brand-canvas min-h-screen">
      <Header />
      <main className="pt-36 md:pt-40 w-full">
        {/* Hero Section */}
        <div className="bg-brand-gradient py-20 md:py-32">
          <div className="px-4 md:px-16 max-w-7xl mx-auto text-center">
            <div className="mb-6">
              <a href="/" className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span className="font-body text-sm">Back to Home</span>
              </a>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-light text-primary-foreground mb-8">
              Get in Touch
            </h1>
            <div className="max-w-3xl mx-auto">
              <p className="font-body text-lg md:text-xl leading-relaxed text-primary-foreground/80">
                Ready to find your perfect address behind the gate? Our team of hyper-local experts is here to help you discover exceptional properties in Nairobi's most coveted neighborhoods.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-brand-surface px-4 md:px-16 py-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-accent" />
                </div>
                <h3 className="font-display text-xl font-light text-primary-foreground mb-2">
                  Phone
                </h3>
                <p className="font-body text-base text-primary-foreground mb-2">
                  +254 712 345 678
                </p>
                <p className="font-body text-sm text-primary-foreground/75">
                  Mon-Fri: 9AM-6PM
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-accent" />
                </div>
                <h3 className="font-display text-xl font-light text-primary-foreground mb-2">
                  Email
                </h3>
                <p className="font-body text-base text-primary-foreground mb-2">
                  info@wildgates.co.ke
                </p>
                <p className="font-body text-sm text-primary-foreground/75">
                  24-48 hour response
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-accent" />
                </div>
                <h3 className="font-display text-xl font-light text-primary-foreground mb-2">
                  Office
                </h3>
                <p className="font-body text-base text-primary-foreground mb-2">
                  Westlands, Nairobi
                </p>
                <p className="font-body text-sm text-primary-foreground/75">
                  By appointment only
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="px-4 md:px-16 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl md:text-4xl font-light text-primary-foreground mb-4">
                Send Us a Message
              </h2>
              <p className="font-body text-base text-primary-foreground/75">
                Tell us about your property needs and we'll get back to you with personalized recommendations
              </p>
            </div>
            
            <div className="bg-card/95 border border-border/20 rounded-xl p-8">
              <form className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-body text-sm font-medium text-foreground mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-border/20 rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-sm font-medium text-foreground mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-border/20 rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                      placeholder="Doe"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-body text-sm font-medium text-foreground mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      className="w-full px-4 py-3 border border-border/20 rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                      placeholder="john.doe@example.com"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-sm font-medium text-foreground mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      className="w-full px-4 py-3 border border-border/20 rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                      placeholder="+254 712 345 678"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block font-body text-sm font-medium text-foreground mb-2">
                    Subject
                  </label>
                  <select className="w-full px-4 py-3 border border-border/20 rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent">
                    <option value="">Select a topic</option>
                    <option value="buy">Property Purchase</option>
                    <option value="rent">Property Rental</option>
                    <option value="invest">Investment Opportunities</option>
                    <option value="consult">General Consultation</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block font-body text-sm font-medium text-foreground mb-2">
                    Message
                  </label>
                  <textarea
                    rows={6}
                    className="w-full px-4 py-3 border border-border/20 rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                    placeholder="Tell us about your property requirements, preferred locations, budget range, and any specific features you're looking for..."
                  ></textarea>
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="newsletter"
                    className="w-4 h-4 text-accent border-border/20 rounded focus:ring-accent"
                  />
                  <label htmlFor="newsletter" className="font-body text-sm text-muted-foreground">
                    I'd like to receive property updates and market insights from Wild Gates
                  </label>
                </div>
                
                <button
                  type="submit"
                  className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground font-body text-sm tracking-wider uppercase py-3 px-8 rounded-lg hover:bg-accent/90 transition-colors"
                >
                  <Send className="w-4 h-4" />
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Office Hours */}
        <div className="bg-brand-surface px-4 md:px-16 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl md:text-4xl font-light text-primary-foreground mb-4">
                Office Hours & Response Times
              </h2>
              <p className="font-body text-base text-primary-foreground/75">
                We're committed to providing timely and personalized service
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-background border border-border/20 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-6 h-6 text-accent" />
                  <h3 className="font-display text-xl font-light text-foreground">
                    Office Hours
                  </h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-foreground">Monday - Friday</span>
                    <span className="text-muted-foreground">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-foreground">Saturday</span>
                    <span className="text-muted-foreground">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-foreground">Sunday</span>
                    <span className="text-muted-foreground">Closed</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-background border border-border/20 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Mail className="w-6 h-6 text-accent" />
                  <h3 className="font-display text-xl font-light text-foreground">
                    Response Times
                  </h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-foreground">Email Inquiries</span>
                    <span className="text-muted-foreground">24-48 hours</span>
                  </div>
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-foreground">Phone Calls</span>
                    <span className="text-muted-foreground">During office hours</span>
                  </div>
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-foreground">Property Viewings</span>
                    <span className="text-muted-foreground">By appointment</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
